import { getOssToken } from '@/api/index';
import * as OSS from 'ali-oss';
import md5 from 'md5-js/md5';
import dayjs from 'dayjs';
import Vue from 'vue';

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

function getUploadOptions(fileSize) {
  if (fileSize < 5 * MB) {
    return { partSize: 1 * MB, parallel: 3 };
  } else if (fileSize < 50 * MB) {
    return { partSize: 2 * MB, parallel: 3 };
  } else if (fileSize < 100 * MB) {
    return { partSize: 5 * MB, parallel: 5 };
  } else if (fileSize < 500 * MB) {
    return { partSize: 10 * MB, parallel: 5 };
  } else if (fileSize < 1 * GB) {
    return { partSize: 20 * MB, parallel: 8 };
  } else {
    const partSize = Math.min(Math.ceil(fileSize / 10000), 5 * GB);
    return { partSize: Math.max(partSize, 100 * KB), parallel: Math.min(Math.ceil(fileSize / (50 * MB)), 10) };
  }
}

export async function uploadSmallFileToOSS(file, dir = '', onProgress) {
  try {
    Vue.prototype.$globalLoading.show('File uploading...', true);

    const date = dayjs().format('YYYYMMDD');
    const { data: ossConfig } = await getOssToken({ dir: `${dir}/${date}` });

    const client = new OSS({
      region: ossConfig.region,
      endpoint: 'https://' + ossConfig.endpoint,
      accessKeyId: ossConfig.accessKeyId,
      accessKeySecret: ossConfig.accessKeySecret,
      stsToken: ossConfig.securityToken,
      bucket: ossConfig.bucket,
      secure: process.env.NODE_ENV !== 'development',
    });

    const nameHash = await getFileHash(file);
    const ext = file.name.split('.').pop();
    const fileName = `${nameHash}.${ext}`;
    const key = `${ossConfig.dir}/${fileName}`;

    const { partSize, parallel } = getUploadOptions(file.size);

    await client.multipartUpload(key, file, {
      partSize,
      parallel,
      headers: {
        'Content-Disposition': `attachment; filename="${encodeURIComponent(file.name)}"`,
      },
      progress: (p) => {
        const percent = Math.round(p * 100);
        Vue.prototype.$globalLoading.updateProgress(percent);
        if (onProgress) onProgress(percent);
      },
    });

    Vue.prototype.$globalLoading.hide();
    return `${ossConfig.oss_url}/${key}`;
  } catch (error) {
    Vue.prototype.$globalLoading.hide();
    console.error('Upload failed:', error);
    Vue.prototype.$layer.msg('Upload failed, please try again later!');
    throw error;
  }
}

export async function uploadLargeFileToOSS(file, dir = '', onProgress) {
  try {
    Vue.prototype.$globalLoading.show('File uploading...', true);

    const date = dayjs().format('YYYYMMDD');
    const { data: ossConfig } = await getOssToken({ dir: `${dir}/${date}` });

    const client = new OSS({
      region: ossConfig.region,
      endpoint: 'https://' + ossConfig.endpoint,
      accessKeyId: ossConfig.accessKeyId,
      accessKeySecret: ossConfig.accessKeySecret,
      stsToken: ossConfig.securityToken,
      bucket: ossConfig.bucket,
      secure: process.env.NODE_ENV !== 'development',
    });

    const nameHash = await getFileHash(file);
    const ext = file.name.split('.').pop();
    const fileName = `${nameHash}.${ext}`;
    const key = `${ossConfig.dir}/${fileName}`;

    const { partSize, parallel } = getUploadOptions(file.size);

    await client.multipartUpload(key, file, {
      partSize,
      parallel,
      headers: {
        'Content-Disposition': `attachment; filename="${encodeURIComponent(file.name)}"`,
      },
      progress: (p) => {
        const percent = Math.round(p * 100);
        Vue.prototype.$globalLoading.updateProgress(percent);
        if (onProgress) onProgress(percent);
      },
    });

    Vue.prototype.$globalLoading.hide();
    return `${ossConfig.oss_url}/${key}`;
  } catch (error) {
    Vue.prototype.$globalLoading.hide();
    console.error('Upload failed:', error);
    Vue.prototype.$layer.msg('Upload failed, please try again later!');
    throw error;
  }
}

export async function uploadFileToOSS(file, dir = '', threshold = 5, onProgress) {
  const fileSizeInMB = file.size / (1024 * 1024);
  if (fileSizeInMB < threshold) {
    return await uploadSmallFileToOSS(file, dir, onProgress);
  } else {
    return await uploadLargeFileToOSS(file, dir, onProgress);
  }
}

async function getFileHash(file) {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-1', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
      return hashHex;
    } catch (error) {
      console.warn('Web Crypto API failed, falling back to MD5', error);
    }
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      try {
        const hash = md5(e.target.result);
        resolve(hash);
      } catch (error) {
        const fallbackHash = btoa(file.name + file.size)
          .replace(/[^a-zA-Z0-9]/g, '')
          .toLowerCase();
        resolve(fallbackHash.substring(0, 40));
      }
    };
    reader.onerror = function () {
      const fallbackHash = btoa(file.name + file.size)
        .replace(/[^a-zA-Z0-9]/g, '')
        .toLowerCase();
      resolve(fallbackHash.substring(0, 40));
    };
    reader.readAsBinaryString(file);
  });
}